import React, { useMemo } from "react";

import { useIntl } from "react-intl";

import todayOrYesterday from "src/libs/datetime";
import TooltipComponent from "src/organization/pages/project/environment/metrics/components/tooltip";
import formatPercentage from "src/organization/pages/project/environment/metrics/utils/formatPercentage";

import * as S from "./Tooltip.styles";

import type { CursorDataSyncType } from "src/organization/pages/project/environment/metrics/http2/types";

interface TooltipProps {
  hosts?: string[] & {
    prefixId?: string | null;
  };
  activeLine?: string;
  tooltipFormatter: (value: number) => string;
  isVisible: boolean;
  cursorData?: CursorDataSyncType;
}

/**
 * @param {string} hostname
 * @returns {string} the hostname limited to 15 characters
 */
const clampHostname = hostname => {
  if (hostname.length <= 15) {
    return hostname;
  }

  const start = hostname.slice(0, 5);
  const end = hostname.slice(-7);

  return `${start}...${end}`;
};

const Tooltip = ({
  hosts,
  activeLine,
  tooltipFormatter,
  isVisible,
  cursorData
}: TooltipProps) => {
  const intl = useIntl();
  const { chartRect, adaptedCursor } = cursorData || {};
  const data = adaptedCursor?.indexedData;

  const rows = useMemo(() => {
    if (!hosts?.length || !data) {
      return [];
    }

    return hosts
      ?.filter(host => data[`${host}@value`] >= 0)
      ?.map(host => {
        const hostName = clampHostname(host);
        return {
          id: host,
          values: {
            percentage: formatPercentage(data[`${host}@percentage`]),
            value: tooltipFormatter(data[`${host}@value`]),
            host:
              host === "average"
                ? intl.formatMessage({ id: "average" })
                : hostName
          }
        };
      });
  }, [data, hosts, intl, tooltipFormatter]);

  if (!isVisible) return null;

  return (
    <TooltipComponent
      isVisible
      adaptedCursor={adaptedCursor}
      chartRect={chartRect}
    >
      <S.ContentWrapper>
        <S.Table>
          <tbody>
            <S.TableRow>
              <td className="percentage" colSpan={3}>
                {todayOrYesterday(data?.date)}
              </td>
            </S.TableRow>
            <S.TableRow>
              <td className="percentage">{tooltipFormatter(data?.max)}</td>
              <td className="value" colSpan={2}>
                Max
              </td>
            </S.TableRow>
            {rows.map(({ id, values }) => (
              <S.TableRow $isActive={id === activeLine} key={id}>
                <td className="percentage">{values.percentage}</td>
                <td className="value">{values.value}</td>
                <td className="host-name">{values.host}</td>
              </S.TableRow>
            ))}
          </tbody>
        </S.Table>
      </S.ContentWrapper>
    </TooltipComponent>
  );
};

export default React.memo(Tooltip);
